import { Routes, Route, Navigate } from "react-router-dom";
import { ApplicationRouter } from "@/components/workspace/ApplicationRouter";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ToastProvider } from "@/context/ToastContext";
import { AppLayout } from "@/AppLayout";
import { Login } from "@/pages/Login";
import { Home } from "@/pages/Home";
import { DevelopmentRoutes } from "@/pages/Developments";
import { TasksRoutes } from "@/pages/Tasks";
import { AiConsumptionPage } from "@/pages/AiConsumption";
import { Settings } from "@/pages/Settings";
import { Tools } from "@/pages/Tools";
import { PasswordsRoutes } from "@/pages/Passwords";
import Test from "@/pages/Test";
import { UsersRoutes } from "@/pages/Users";
import { RolesRoutes } from "@/pages/Roles";
import { CompaniesRoutes } from "@/pages/Companies";
import { Menus } from "@/pages/Companies/Menus";
import { ClientsRoutes } from "@/pages/Clients";
import { ProvidersRoutes } from "@/pages/Providers";
import { TendersRoutes } from "@/pages/Tenders";
import { StudiesRoutes } from "@/pages/Studies";
import { ForcePasswordChange } from "@/pages/Auth/ForcePasswordChange";
import { Versions } from "@/pages/Versions";
import "@/css/app.css";

const TasksGuarded = () => {
    const { hasTasks } = useAuth();
    return (
        <RoleRoute allowed={hasTasks}>
            <AppLayout>
                <TasksRoutes />
            </AppLayout>
        </RoleRoute>
    );
};

const DevelopmentGuarded = () => {
    return (
        <AppLayout>
            <DevelopmentRoutes />
        </AppLayout>
    );
};

const AiConsumptionGuarded = () => {
    const { isIt } = useAuth();
    return (
        <RoleRoute allowed={isIt}>
            <AppLayout>
                <Routes>
                    <Route
                        path="/"
                        element={<Navigate to="/ai/data" replace />}
                    />
                    <Route path="/:tab" element={<AiConsumptionPage />} />
                </Routes>
            </AppLayout>
        </RoleRoute>
    );
};

const AdminOrItGuarded = ({ children }) => {
    const { isIt } = useAuth();
    return <RoleRoute allowed={isIt}>{children}</RoleRoute>;
};

const ProtectedRoute = ({ children }) => {
    const { user, token, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-page">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary"></div>
            </div>
        );
    }

    if (!token) {
        return <Navigate to="/login" replace />;
    }

    if (user?.needs_password_change) {
        return <ForcePasswordChange />;
    }

    return children;
};

const RoleRoute = ({ children, allowed }) => {
    const { loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-page">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary"></div>
            </div>
        );
    }

    if (!allowed) {
        return <Navigate to="/" replace />;
    }

    return children;
};

const PublicRoute = ({ children }) => {
    const { token, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-page">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary"></div>
            </div>
        );
    }

    if (token) {
        return <Navigate to="/" replace />;
    }

    return children;
};

const ClientsGuarded = () => {
    return (
        <AppLayout>
            <ClientsRoutes />
        </AppLayout>
    );
};

const TendersGuarded = () => {
    return (
        <AppLayout>
            <TendersRoutes />
        </AppLayout>
    );
};

const StudiesGuarded = () => {
    return (
        <AppLayout>
            <StudiesRoutes />
        </AppLayout>
    );
};

export function App() {
    return (
        <ToastProvider>
            <AuthProvider>
                <ApplicationRouter>
                    <Routes>
                        {/* Public Login Route */}
                        <Route
                            path="/login"
                            element={
                                <PublicRoute>
                                    <Login />
                                </PublicRoute>
                            }
                        />

                        <Route
                            path="/providers/*"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <ProvidersRoutes />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/navigation"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>{null}</AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Home Route */}
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Home />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/developments/*"
                            element={
                                <ProtectedRoute>
                                    <DevelopmentGuarded />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/tasks/*"
                            element={
                                <ProtectedRoute>
                                    <TasksGuarded />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/ai/*"
                            element={
                                <ProtectedRoute>
                                    <AiConsumptionGuarded />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/passwords/*"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <PasswordsRoutes />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/users/*"
                            element={
                                <ProtectedRoute>
                                    <AdminOrItGuarded>
                                        <AppLayout>
                                            <UsersRoutes />
                                        </AppLayout>
                                    </AdminOrItGuarded>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/roles/*"
                            element={
                                <ProtectedRoute>
                                    <AdminOrItGuarded>
                                        <AppLayout>
                                            <RolesRoutes />
                                        </AppLayout>
                                    </AdminOrItGuarded>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/companies/*"
                            element={
                                <ProtectedRoute>
                                    <AdminOrItGuarded>
                                        <AppLayout>
                                            <CompaniesRoutes />
                                        </AppLayout>
                                    </AdminOrItGuarded>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/menus/*"
                            element={
                                <ProtectedRoute>
                                    <AdminOrItGuarded>
                                        <AppLayout>
                                            <Menus />
                                        </AppLayout>
                                    </AdminOrItGuarded>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/clients/*"
                            element={
                                <ProtectedRoute>
                                    <ClientsGuarded />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/tenders/*"
                            element={
                                <ProtectedRoute>
                                    <TendersGuarded />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/studies/*"
                            element={
                                <ProtectedRoute>
                                    <StudiesGuarded />
                                </ProtectedRoute>
                            }
                        />

                        {/* Test Route for downloaded TSX */}
                        <Route
                            path="/test"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Test />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Protected User Settings Routes */}
                        <Route
                            path="/tools/*"
                            element={
                                <ProtectedRoute>
                                    <AdminOrItGuarded>
                                        <AppLayout>
                                            <Tools />
                                        </AppLayout>
                                    </AdminOrItGuarded>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Settings />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/:tab"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Settings />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/:tab/:subtab"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Settings />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route path="/versions" element={<Versions />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </ApplicationRouter>
            </AuthProvider>
        </ToastProvider>
    );
}

export default App;
